// Procedural small craters for the Moon, below what the real data resolves.
//
// Everything the LOLA heights and the WAC imagery can show comes from them
// (moondem.js, the terrain material). Those resolve craters down to about
// 700 m in relief and 330 m in the imagery, so this field only adds craters
// smaller than MAX_D (255 m). How many it adds is measured, not tuned:
//
//   cumulative density N(>D) per km^2 = min(N1(T) * F(D), Neq(D))
//
// F is the Neukum (2001) lunar production function normalised to F(1 km) = 1,
// N1(T) the Neukum chronology (craters over 1 km per km^2 on a surface T Gyr
// old), and Neq = 0.077 D^-2 the empirical equilibrium where new craters erase
// old ones as fast as they form (about 5 % of geometric saturation). Below a
// few hundred metres both maria and highlands sit at equilibrium, so the
// terrain age (T from the local albedo: dark maria ~3.5 Gyr, bright highlands
// ~4.2 Gyr) changes the count only in the upper classes. See
// scripts/rocketry-tests/crater-density.mjs for the measured table.
//
// Craters live on a jittered 3D lattice per size class, one candidate per
// cell, kept when its centre lies within half a cell of the sphere (so the
// areal density is exactly p / cell^2) and projected onto it. Each crater has
// its own diameter inside the class bin, a degradation state drawn so most
// are old and soft and few are fresh (fresh: depth/D 0.2, sharp raised rim,
// an ejecta apron to three radii; degraded: depth/D down to 0.025, rim gone,
// walls rounded), a slight ellipticity, and in the smallest classes a flat
// floor or a bench where the bowl reaches the bedrock under the regolith.
//
// craterHeight(dx, dy, dz, radius, minFeature, ageAt) returns metres of relief
// at the unit direction; ageAt(dx, dy, dz) gives the surface age in Gyr (null
// for highland statistics). craterFreshness() returns how much fresh-crater
// ejecta covers the point, which the terrain shades brighter.

// The imagery shows craters from about 330 m (four pixels of 83 m): the
// largest class bin (180 m, up to 180 * sqrt2) stops below that.
export const MAX_D = 255;         // m: larger craters come from the data
// Down to 2.75 m: at equilibrium a crater over a metre sits in every dozen
// square metres, and the two smallest classes are what pockmark the ground
// in the Apollo frames under a high sun. They are shading only (the mesh
// carries nothing under 30 m) and fade out below a pixel and a half.
const DIAMETERS = [180, 90, 45, 22, 11, 5.5, 2.75];

// Neukum (2001) production function coefficients, log10 N = a0 + sum a_n (log10 D)^n.
const NPF = [-3.557528, 0.781027, 1.021521, -0.156012, -0.444058, 0.019977, 0.086850, -0.005874, -0.006809, 8.25e-4, 5.54e-5];
export function productionRel(Dkm) {
  const l = Math.log10(Dkm);
  let s = 0, p = l;
  for (const a of NPF) { s += a * p; p *= l; }
  return 10 ** s;
}
export const chronologyN1 = (T) => 5.44e-14 * (Math.exp(6.93 * T) - 1) + 8.38e-4 * T;
export const equilibriumN = (Dkm) => 0.077 / (Dkm * Dkm);
export const cumulativeN = (Dkm, T) => Math.min(chronologyN1(T) * productionRel(Dkm), equilibriumN(Dkm));

const T_MAX = 4.2;

// Classes: nominal diameter D (bin D/sqrt2 .. D*sqrt2), lattice cell (m), and
// the probability a cell holds a crater at T_MAX. The cell is sized for a
// probability of 0.9 but never under a crater's reach (three radii of the
// largest in the bin), so the 3x3x3 neighbourhood always finds every crater
// that touches a point.
const CLASSES = DIAMETERS.map((D, i) => {
  const lo = D / Math.SQRT2 / 1000, hi = D * Math.SQRT2 / 1000;
  const perKm2 = cumulativeN(lo, T_MAX) - cumulativeN(hi, T_MAX);
  const cell = Math.max(D * 2.15, Math.sqrt(0.9 / perKm2) * 1000);
  const p = perKm2 * (cell / 1000) ** 2;
  return { d: D, cell, p, seed: i * 7 + 1 };
});
export const CRATER_CLASSES = CLASSES;

// Fraction of the T_MAX count a surface of age T holds in a class.
function ageFactor(K, T) {
  if (T >= T_MAX) return 1;
  const lo = K.d / Math.SQRT2 / 1000, hi = K.d * Math.SQRT2 / 1000;
  return (cumulativeN(lo, T) - cumulativeN(hi, T)) / (cumulativeN(lo, T_MAX) - cumulativeN(hi, T_MAX));
}

function hash(ix, iy, iz, k) {
  let h = Math.imul(ix, 374761393) ^ Math.imul(iy, 668265263) ^ Math.imul(iz, 2147483647) ^ Math.imul(k, 1274126177);
  h = Math.imul(h ^ (h >>> 13), 1103515245);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

// Relief of one crater (metres) at distance r (m) from its centre.
function profile(r, R, depth, rim, fresh, floor) {
  const q = r / R;
  // Wall: fresh craters are parabolic bowls with steep upper walls, old ones
  // cosine dishes. The floor clips the bowl (bedrock under the regolith).
  const k = 2 + 2.5 * fresh;
  if (q < 1) {
    let h = -depth + (depth + rim) * Math.pow(q, k);
    if (floor > 0) h = Math.max(h, -depth * (1 - floor));
    // Round the crest over the last tenth of the radius on softer craters.
    const crest = 0.25 - 0.18 * fresh;
    if (q > 1 - crest) {
      const t = (q - (1 - crest)) / crest;
      h = h * (1 - t * t * (1 - fresh) * 0.6) + rim * t * t * (1 - fresh) * 0.6;
    }
    return h;
  }
  // Outside: the rim falls away, then a thin ejecta apron falling as q^-3.
  const w = 0.18 + 0.5 * (1 - fresh);
  const fall = Math.exp(-(((q - 1) / w) ** 2));
  const apron = fresh * rim * 0.35 / (q * q * q);
  return rim * fall + (q < 3 ? apron * (1 - (q - 1) / 2) : 0);
}

function visit(dx, dy, dz, bodyRadius, minFeature, ageAt, fn) {
  for (let c = 0; c < CLASSES.length; c++) {
    const K = CLASSES[c];
    if (K.d < minFeature) break;           // classes run largest first
    const f = bodyRadius / K.cell;           // cells per unit direction
    const px = dx * f, py = dy * f, pz = dz * f;
    const ix = Math.floor(px), iy = Math.floor(py), iz = Math.floor(pz);
    for (let a = -1; a <= 1; a++) for (let b = -1; b <= 1; b++) for (let e = -1; e <= 1; e++) {
      const cx = ix + a, cy = iy + b, cz = iz + e;
      if (hash(cx, cy, cz, K.seed) > K.p) continue;
      const ox = cx + hash(cx, cy, cz, K.seed + 1), oy = cy + hash(cx, cy, cz, K.seed + 2), oz = cz + hash(cx, cy, cz, K.seed + 3);
      // Keep only centres within half a cell of the sphere, then project.
      const len = Math.sqrt(ox * ox + oy * oy + oz * oz);
      if (Math.abs(len - f) > 0.5) continue;
      const s = f / len;
      const qx = ox * s, qy = oy * s, qz = oz * s;
      const ddx = (px - qx) * K.cell, ddy = (py - qy) * K.cell, ddz = (pz - qz) * K.cell;
      const reach = K.d * Math.SQRT2 * 1.5;
      if (Math.abs(ddx) > reach || Math.abs(ddy) > reach || Math.abs(ddz) > reach) continue;
      const u = hash(cx, cy, cz, K.seed + 4);
      if (ageAt && K.d > 60) {
        const T = ageAt(qx / f, qy / f, qz / f);
        if (T != null && u > ageFactor(K, T)) continue;
      }
      fn(K, cx, cy, cz, ddx, ddy, ddz);
    }
  }
}

export function craterHeight(dx, dy, dz, bodyRadius, minFeature = 0, ageAt = null) {
  let sum = 0;
  visit(dx, dy, dz, bodyRadius, minFeature, ageAt, (K, cx, cy, cz, ddx, ddy, ddz) => {
    // Diameter log-uniform over the class bin.
    const D = K.d * Math.pow(2, hash(cx, cy, cz, K.seed + 5) - 0.5);
    const R = D / 2;
    // Mostly degraded: fresh = u^5 puts seven in eight below 0.5.
    const fresh = Math.pow(hash(cx, cy, cz, K.seed + 6), 5);
    // Slight ellipticity along a random tangent-ish axis.
    const ea = hash(cx, cy, cz, K.seed + 7) * Math.PI, e = 0.12 * hash(cx, cy, cz, K.seed + 8);
    const ax = Math.cos(ea), ay = Math.sin(ea) * 0.7071, az = Math.sin(ea) * 0.7071;
    const along = ddx * ax + ddy * ay + ddz * az;
    const r2 = ddx * ddx + ddy * ddy + ddz * ddz;
    const r = Math.sqrt(Math.max(0, r2 + along * along * ((1 + e) ** -2 - 1)));
    if (r > R * 3) return;
    const depth = D * (0.025 + 0.175 * Math.pow(fresh, 0.7));
    const rim = D * (0.004 + 0.036 * fresh);
    // Regolith 4-10 m thick: small bowls that reach bedrock get a flat floor.
    const regolith = 4 + 6 * hash(cx, cy, cz, K.seed + 9);
    const floor = D < regolith * 10 && fresh > 0.2 ? 0.35 : 0;
    sum += profile(r, R, depth, rim, fresh, floor);
  });
  return sum;
}

// 0..1: how much bright, fresh ejecta covers this point.
export function craterFreshness(dx, dy, dz, bodyRadius, minFeature = 0, ageAt = null) {
  let f = 0;
  visit(dx, dy, dz, bodyRadius, Math.max(minFeature, 40), ageAt, (K, cx, cy, cz, ddx, ddy, ddz) => {
    const fresh = Math.pow(hash(cx, cy, cz, K.seed + 6), 5);
    if (fresh < 0.35) return;
    const D = K.d * Math.pow(2, hash(cx, cy, cz, K.seed + 5) - 0.5);
    const q = Math.sqrt(ddx * ddx + ddy * ddy + ddz * ddz) / (D / 2);
    if (q > 3) return;
    f = Math.max(f, (fresh - 0.35) / 0.65 * (q < 1 ? 0.8 : Math.max(0, 1 - (q - 1) / 2)));
  });
  return f;
}
